import type { AppointmentItem } from '../../appointments/appointments-model'
import {
  appointmentTimeLabel,
  calendarDayNumber,
  countLabel,
  formatCount,
  isSameMonth,
  shortCalendarDate,
} from '../calendar-model'
import { StatusMeter, statusCounts } from './StatusMeter'
import styles from './CalendarMonthView.module.css'

/** A light day lists its visits; a busier one shows its count and status mix. */
const LISTED_APPOINTMENTS = 3
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

export function CalendarMonthView({
  days,
  anchor,
  today,
  appointmentsByDate,
  onOpenDay,
}: {
  days: string[]
  anchor: string
  today: string
  appointmentsByDate: Map<string, AppointmentItem[]>
  onOpenDay: (date: string) => void
}) {
  return (
    <section className={styles.month} aria-label="Month calendar">
      <div className={styles.weekdays} aria-hidden="true">
        {WEEKDAYS.map((day) => (
          <span key={day}>{day}</span>
        ))}
      </div>
      <div className={styles.grid}>
        {days.map((date) => {
          const items = [...(appointmentsByDate.get(date) ?? [])].sort(
            (left, right) =>
              (left.scheduledAtMs ?? 0) - (right.scheduledAtMs ?? 0)
          )
          const counts = statusCounts(items)
          const outside = !isSameMonth(date, anchor)
          return (
            <button
              type="button"
              key={date}
              className={styles.cell}
              data-outside={outside ? 'true' : undefined}
              data-today={date === today ? 'true' : undefined}
              aria-current={date === today ? 'date' : undefined}
              aria-label={`${shortCalendarDate(date)}: ${items.length ? `${countLabel(items.length, 'appointment')}, ${counts.confirmed} confirmed` : 'no appointments'}`}
              onClick={() => onOpenDay(date)}
            >
              <span className={styles.dayNumber}>
                {calendarDayNumber(date)}
              </span>
              {items.length > 0 && items.length <= LISTED_APPOINTMENTS ? (
                <span className={styles.miniCount} aria-hidden="true">
                  {items.length}
                </span>
              ) : null}
              {items.length > 0 && items.length <= LISTED_APPOINTMENTS ? (
                <span className={styles.entries}>
                  {items.map((item) => (
                    <span
                      className={styles.entry}
                      data-status={item.status}
                      key={item.appointmentId}
                    >
                      <time>{appointmentTimeLabel(item)}</time>
                      <span>{item.patientDisplayName}</span>
                    </span>
                  ))}
                </span>
              ) : null}
              {items.length > LISTED_APPOINTMENTS ? (
                <span className={styles.summary}>
                  <span className={styles.count}>
                    <strong>{formatCount(items.length)}</strong>
                    <small>appointments</small>
                  </span>
                  <StatusMeter counts={counts} />
                  <small className={styles.confirmed}>
                    {formatCount(counts.confirmed)} confirmed
                  </small>
                </span>
              ) : null}
            </button>
          )
        })}
      </div>
    </section>
  )
}
